import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { Loading } from "spark-core-dx/components";

const css = (name) => `var(${name})`;
const ECHARTS_MAIN_PROCESS_KEY = "__flagInMainProcess";

const getMessageTone = (error, isLoading) => {
  if (error) {
    return css("--error");
  }

  if (isLoading) {
    return css("--text-primary");
  }

  return css("--secondary-text");
};

export default function EChartsCanvas({
  renderModel,
  isLoading = false,
  status = "",
  error = "",
  style = { width: "100%", height: "100%" },
}) {
  const wrapperRef = useRef(null);
  const elRef = useRef(null);
  const chartRef = useRef(null);
  const lastInit = useRef({ theme: null, renderer: null, dpr: null });
  const applyTokenRef = useRef(0);
  const optionRetryTimeoutRef = useRef(0);
  const optionFrameRef = useRef(0);

  const hasOption = !!renderModel?.OptionJson;
  const parsedOption = useMemo(() => {
    if (!hasOption) {
      return null;
    }

    try {
      const raw = renderModel.OptionJson;
      const option =
        typeof raw === "string" ? JSON.parse(raw) : JSON.parse(JSON.stringify(raw));

      if (!option.backgroundColor) {
        option.backgroundColor = "transparent";
      }

      return option;
    } catch {
      return { __invalid: true };
    }
  }, [hasOption, renderModel?.OptionJson]);

  const runWhenChartReady = (chart, action, token, attempt = 0) => {
    if (!chart || chart.isDisposed() || chartRef.current !== chart || applyTokenRef.current !== token) {
      return;
    }

    if (chart[ECHARTS_MAIN_PROCESS_KEY]) {
      if (attempt >= 20) {
        return;
      }

      if (optionRetryTimeoutRef.current) {
        window.clearTimeout(optionRetryTimeoutRef.current);
      }
      optionRetryTimeoutRef.current = window.setTimeout(
        () => runWhenChartReady(chart, action, token, attempt + 1),
        40
      );
      return;
    }

    if (optionFrameRef.current) {
      window.cancelAnimationFrame(optionFrameRef.current);
    }

    optionFrameRef.current = window.requestAnimationFrame(() => {
      optionFrameRef.current = 0;

      if (
        !chart ||
        chart.isDisposed() ||
        chartRef.current !== chart ||
        applyTokenRef.current !== token
      ) {
        return;
      }

      if (chart[ECHARTS_MAIN_PROCESS_KEY]) {
        runWhenChartReady(chart, action, token, attempt + 1);
        return;
      }

      action(chart);
    });
  };

  useEffect(() => {
    if (!hasOption || !elRef.current) {
      return;
    }

    const option = parsedOption;

    if (option?.__invalid) {
      const chart = chartRef.current;
      if (chart && !chart.isDisposed()) {
        chart.clear();
      }
      return;
    }

    let optionTimeoutId = 0;

    const applyToken = applyTokenRef.current + 1;
    applyTokenRef.current = applyToken;

    optionTimeoutId = window.setTimeout(() => {
      if (!elRef.current) {
        return;
      }

      const theme = renderModel?.Theme || "default";
      const renderer = (renderModel?.Renderer || "canvas").toLowerCase();
      const dpr = renderModel?.DevicePixelRatio || window.devicePixelRatio || 1;

      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }

      try {
        const liveChart = echarts.init(elRef.current, theme, {
          renderer,
          devicePixelRatio: dpr,
          useDirtyRect: true,
        });
        chartRef.current = liveChart;
        lastInit.current = { theme, renderer, dpr };
        runWhenChartReady(liveChart, (readyChart) => {
          readyChart.setOption(option, {
            notMerge: true,
            lazyUpdate: renderModel?.LazyUpdate !== false,
            silent: !!renderModel?.Silent,
          });
        }, applyToken);
      } catch {
        if (chartRef.current && !chartRef.current.isDisposed()) {
          chartRef.current.clear();
        }
      }
    }, 60);

    return () => {
      if (optionTimeoutId) {
        window.clearTimeout(optionTimeoutId);
      }
      if (optionRetryTimeoutRef.current) {
        window.clearTimeout(optionRetryTimeoutRef.current);
        optionRetryTimeoutRef.current = 0;
      }
      if (optionFrameRef.current) {
        window.cancelAnimationFrame(optionFrameRef.current);
        optionFrameRef.current = 0;
      }
    };
  }, [
    hasOption,
    parsedOption,
    renderModel?.DevicePixelRatio,
    renderModel?.LazyUpdate,
    renderModel?.Renderer,
    renderModel?.Silent,
    renderModel?.Theme,
  ]);

  useEffect(() => {
    const safeResize = () => {
      const chart = chartRef.current;
      if (!chart || chart.isDisposed()) {
        return;
      }

      try {
        const host = chart.getDom();
        const width = host?.clientWidth ?? 0;
        const height = host?.clientHeight ?? 0;
        if (width > 0 && height > 0) {
          runWhenChartReady(chart, (readyChart) => {
            readyChart.resize();
          }, applyTokenRef.current);
        }
      } catch {
      }
    };

    let resizeScheduleTimeoutId = 0;
    const schedule = () => {
      if (resizeScheduleTimeoutId) {
        window.clearTimeout(resizeScheduleTimeoutId);
      }

      resizeScheduleTimeoutId = window.setTimeout(safeResize, 180);
    };

    const targets = [wrapperRef.current].filter(Boolean);
    const observer = new ResizeObserver(schedule);
    targets.forEach((target) => observer.observe(target));

    window.addEventListener("resize", schedule);
    document.addEventListener("fullscreenchange", schedule);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        schedule();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    targets.forEach((target) => target.addEventListener("transitionend", schedule));
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      document.removeEventListener("fullscreenchange", schedule);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      targets.forEach((target) => target.removeEventListener("transitionend", schedule));
      if (resizeScheduleTimeoutId) {
        window.clearTimeout(resizeScheduleTimeoutId);
      }
    };
  }, []);

  useEffect(() => () => {
    if (chartRef.current) {
      chartRef.current.dispose();
      chartRef.current = null;
    }
    if (optionRetryTimeoutRef.current) {
      window.clearTimeout(optionRetryTimeoutRef.current);
      optionRetryTimeoutRef.current = 0;
    }
    if (optionFrameRef.current) {
      window.cancelAnimationFrame(optionFrameRef.current);
      optionFrameRef.current = 0;
    }
  }, []);

  const message = error || status || "Preview unavailable.";
  const showOverlay = isLoading || parsedOption?.__invalid;
  const showMessageOnly = !hasOption && !showOverlay;

  return (
    <div
      ref={wrapperRef}
      style={{
        ...style,
        position: "relative",
        overflow: "hidden",
        background: "var(--primary-background)",
      }}
    >
      <div
        ref={elRef}
        style={{
          position: "absolute",
          inset: 0,
          opacity: hasOption ? 1 : 0,
        }}
      />

      {showOverlay ? (
        <div
          aria-live="polite"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            background: parsedOption?.__invalid ? "transparent" : css("--chart-loading-bg"),
            color: getMessageTone(error, isLoading),
            textAlign: "center",
          }}
        >
          <div
            style={{
              maxWidth: 520,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
            }}
          >
            {isLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Loading type="spinner-2" size="1.5rem" />
              </div>
            ) : null}
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {parsedOption?.__invalid ? "We could not show this chart right now." : message}
            </div>
          </div>
        </div>
      ) : null}

      {showMessageOnly ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.5rem",
            color: getMessageTone(error, false),
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          <div style={{ maxWidth: "32rem" }}>{message}</div>
        </div>
      ) : null}
    </div>
  );
}
